import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import { AppRouteBody } from '@/components/AppRouteBody';
import { technologyExportConfig } from '@/components/export/configs/technology.export.config';
import {
  buildActiveFilters,
  UniversalExportModal,
} from '@/components/export/universal';
import { FilterDropdown } from '@/components/filters/FilterDropdown';
import type { FilterOption, GroupOption } from '@/components/filters/types';
import { ClearFiltersButton } from '@/components/legacy-ui/ClearFiltersButton';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { Loader } from '@/components/Loader';
import { GetStartedModal } from '@/components/modals/GetStartedModal';
import { NoteModal } from '@/components/modals/NoteModal';
import { SelectionBanner } from '@/components/table/SelectionBanner';
import { Table } from '@/components/table/Table';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { useGetTechnologyFilters } from '@/hooks/api/useGetTechnologyFilters';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useHasActiveFilters } from '@/hooks/useHasActiveFilters';
import { useMy } from '@/hooks/useMy';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { Filter, useGetTechnologies } from '@/hooks/useTechnology';
import { ImportAssetsModal } from '@/sections/asset/components/modals/ImportAssetsModal';
import {
  ASSET_GET_STARTED_METHODS,
  type AssetGetStartedMethodId,
} from '@/sections/asset/config/getStartedMethods';
import { AddSeedByTypeModal } from '@/sections/seeds/components/modals/AddSeedByTypeModal';
import { TechnologyActions } from '@/sections/technology/components/actions';
import {
  TechnologyRemoveTag,
  TechnologyTag,
} from '@/sections/technology/components/modals';
import { getFilterDropdownWidth } from '@/sections/technology/config';
import {
  useTechnologyActions,
  useTechnologyColumns,
} from '@/sections/technology/hooks';
import { useGlobalState } from '@/state/global.state';
import { GraphQuery, Technology } from '@/types';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { capitalize, omit } from '@/utils/lodash.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

import { getTechnologyNoDataConfig } from './utils/getTechnologyNoDataConfig';

export function Techonologies() {
  const navigate = useNavigate();
  const { drawerState } = useGlobalState();
  const queryClient = useQueryClient();
  const { canWriteAssets } = useEntitlements();

  // GetStartedModal workflow state
  const [isGetStartedModalOpen, setIsGetStartedModalOpen] = useState(false);
  const [isAddSeedModalOpen, setIsAddSeedModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const [filter, setFilter] = useStorage<Filter>(
    {
      queryKey: 'technologyFilters',
      key: StorageKey.FILTERS_TECHNOLOGIES,
      ephemeralKeys: ['search'],
    },
    {
      sort: {
        orderBy: 'name',
        descending: true,
      },
    }
  );

  const {
    data: technologies,
    status: technologiesStatus,
    error: technologiesError,
    isAllEmpty,
    isFilteredEmpty,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
    activeQuery,
  } = useGetTechnologies({ filter });

  // Detect initial loading state to prevent table UI flicker
  const isInitialLoading = technologiesStatus === 'pending';

  // Determine if any filters are currently active
  const hasActiveFilters = useHasActiveFilters({
    search: filter.search,
    advancedFilters: filter.properties,
  });

  const { data: propertyFilters, status: propertyFiltersStatus } =
    useGetTechnologyFilters();

  // Filter options for export modal pill labels (cached — no extra API call)
  const { filterOptions: techFilterOptions, sortedOptions: techSortedOptions } =
    useEntityFilters('Technology');
  const techFieldLabels = useMemo(
    () => Object.fromEntries(techSortedOptions.map(o => [o.value, o.label])),
    [techSortedOptions]
  );

  // Use custom hook with smart column positioning
  const {
    columns,
    activeColumns,
    setActiveColumns,
    placeholder,
    sortableColumnsConfig,
  } = useTechnologyColumns();

  // Row selection management — uses useBulkSelection for Gmail-style two-step selection
  const bulkSelection = useBulkSelection({
    data: technologies,
    activeFilters: (activeQuery ?? {}) as GraphQuery,
    filterVersion: filter,
  });

  const {
    selectedRows: selectedKeys,
    setSelectedRows: setSelectedKeys,
    hasSelection: isRowsSelected,
    clearSelection,
    isSelectAll,
    isAllLoadedSelected,
    selectAllMatching,
    selectedCount,
    toggleKey,
  } = bulkSelection;

  // Derive selectedTechnologies from selectedRows for action handlers
  const selectedTechnologies = technologies.filter(t =>
    selectedKeys.includes(t.key)
  );

  // Fetch total technology count from database via statistics
  // Backend creates: type="technology", name="vendor" with counts per vendor
  // Sum all vendor counts to get total technology count
  const { data: technologyStatistics } = useMy(
    {
      resource: 'statistic',
      query: '#technology#vendor#now#',
    },
    {
      staleTime: 60000, // Cache for 60 seconds
      refetchInterval: false, // Override default 30s polling
    }
  );

  // Compute total count from statistics response
  // Each vendor has a count - sum them for total technologies
  const totalDatabaseCount = useMemo(() => {
    if (!technologyStatistics) return 0;
    return technologyStatistics.reduce(
      (sum, item) => sum + (item.data?.count || 0),
      0
    );
  }, [technologyStatistics]);

  // Compute export active filters for UniversalExportModal
  const exportActiveFilters = useMemo(
    () =>
      buildActiveFilters(
        filter.properties as Record<string, unknown> | undefined,
        (techFilterOptions as Record<string, (FilterOption | GroupOption)[]>) ??
          {},
        filter.search,
        techFieldLabels
      ),
    [filter.search, filter.properties, techFilterOptions, techFieldLabels]
  );

  // Actions hook with modal state and handlers
  const { modalState, modalHandlers, handlers, selectedRowsCount } =
    useTechnologyActions({
      selectedTechnologies,
      selectedRowsCount: selectedCount,
      invalidateTechnologies: () => {
        queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'technology';
          },
          refetchType: 'active',
        });
      },
      setConfirmStatus: () => {
        // Placeholder for confirm status handling
      },
    });

  // Handle Get Started method selection
  const handleGetStartedMethodSelect = useCallback(
    (methodId: AssetGetStartedMethodId) => {
      setIsGetStartedModalOpen(false);

      switch (methodId) {
        case 'integrations': {
          // Build URL with drawer state params for atomic navigation + drawer open
          const drawerParams = new URLSearchParams();
          drawerParams.set('drawerCompositeKey', 'integration');
          drawerParams.set('drawerOrder', JSON.stringify(['integration']));

          navigate(
            `../${AuthenticatedRoute.INTEGRATIONS}?${drawerParams.toString()}`,
            { relative: 'path' }
          );
          break;
        }
        case 'seeds':
          setIsAddSeedModalOpen(true);
          break;
        case 'import':
          setIsImportModalOpen(true);
          break;
      }
    },
    [navigate]
  );

  // Force table remount on empty → data transition (fixes rendering bug)
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    technologies.length
  );

  // Generate stable key based on filter/sort state to force remount on changes
  // This resets scroll position and clears stale virtualizer state
  // Note: search is excluded - debounced at API level, shouldn't trigger remounts
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        properties: filter.properties,
        sort: filter.sort,
      }),
    [filter.properties, filter.sort]
  );

  // Combine with existing tableKey for empty→data transitions
  const combinedTableKey = `${tableKey}-${filterKey}`;

  // Keyboard shortcuts for bulk actions
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger if Alt key is pressed and there are selected rows
      if (!e.altKey || !isRowsSelected) return;

      // Ignore if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'c':
          // Alt+C - Open note modal (requires write_assets)
          if (!canWriteAssets) return;
          e.preventDefault();
          modalHandlers.openNoteModal();
          break;
        case 't':
          // Alt+T - Open tag modal (requires write_assets)
          if (!canWriteAssets) return;
          e.preventDefault();
          modalHandlers.openTagModal();
          break;
        case 'e':
          // Alt+E - Export selected
          e.preventDefault();
          modalHandlers.openExportModal();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRowsSelected, modalHandlers, handlers, canWriteAssets]);

  return (
    <AppRouteBody title="Technologies">
      {isInitialLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loader isLoading type="spinner" />
        </div>
      ) : (
        <>
          {!isAllEmpty && (
            <TableControlsContainer
              data-testid="table-controls"
              filterLabel={true}
              filters={
                <>
                  {Object.entries(propertyFilters).map(
                    ([key, propertyFilterOptions], index) => (
                      <FilterDropdown
                        key={index}
                        id={key}
                        isLoading={propertyFiltersStatus === 'pending'}
                        label={capitalize(key)}
                        options={propertyFilterOptions}
                        selectedValues={filter?.properties?.[key] || []}
                        onApply={selected => {
                          setFilter(prev => {
                            return {
                              ...prev,
                              properties:
                                selected.length > 0
                                  ? {
                                      ...prev?.properties,
                                      [key]: selected.map(
                                        option => option.value as string
                                      ),
                                    }
                                  : omit(prev?.properties, key),
                            };
                          });
                        }}
                        onClear={() => {
                          setFilter(prev => ({
                            ...prev,
                            properties: omit(prev?.properties, key),
                          }));
                        }}
                        width={getFilterDropdownWidth(key)}
                      />
                    )
                  )}
                  {filter?.properties &&
                    Object.keys(filter?.properties).length > 0 && (
                      <ClearFiltersButton
                        onClick={() =>
                          setFilter(filters => {
                            return {
                              ...filters,
                              properties: {},
                            };
                          })
                        }
                      />
                    )}
                </>
              }
              filtersRight={
                <SearchBar
                  size="sm"
                  className="h-fit w-64"
                  placeholder="Search"
                  value={filter?.search}
                  onChange={value => {
                    setFilter(filter => ({
                      ...filter,
                      search: value,
                    }));
                  }}
                />
              }
              selectionBar={
                <>
                  <TableSelectionBar
                    embedded={true}
                    alwaysVisible={true}
                    isVisible={isRowsSelected}
                    selectedCount={selectedCount}
                    totalCount={technologies.length}
                    hasNextPage={hasNextPage}
                    totalDatabaseLabel={`${totalDatabaseCount.toLocaleString()} total technologies`}
                    resourceLabel="technologies"
                    emptySelectionMessage="Select technologies to perform bulk actions"
                    actions={
                      <TechnologyActions
                        selectedTechnologies={selectedTechnologies}
                        selectedRowsCount={selectedRowsCount}
                        modalHandlers={modalHandlers}
                        actionHandlers={handlers}
                        onClearSelection={clearSelection}
                        hideActions={false}
                      />
                    }
                  />
                  <SelectionBanner
                    selectedCount={selectedCount}
                    totalLoadedCount={technologies.length}
                    hasNextPage={hasNextPage}
                    isSelectAll={isSelectAll}
                    isAllLoadedSelected={isAllLoadedSelected}
                    resourceLabel="technologies"
                    onSelectAllMatching={selectAllMatching}
                    onClearSelection={clearSelection}
                    enableSelectAllMatching={activeQuery != null}
                  />
                </>
              }
            />
          )}
          <Table<Technology>
            key={combinedTableKey}
            keyboardNavigation={true}
            onRowClick={technology => {
              drawerState.handleOpenTechnologyDrawer(technology);
            }}
            rowActions={technology => {
              return {
                menu: {
                  items: [
                    ...(canWriteAssets
                      ? [
                          {
                            label: 'Add Note',
                            onClick: () => {
                              // Select this technology if not already selected
                              if (!selectedKeys.includes(technology.key)) {
                                toggleKey(technology.key);
                              }
                              modalHandlers.openNoteModal();
                            },
                          },
                          {
                            label: 'Add Tag',
                            onClick: () => {
                              if (!selectedKeys.includes(technology.key)) {
                                toggleKey(technology.key);
                              }
                              modalHandlers.openTagModal();
                            },
                          },
                          {
                            label: 'Remove Tag',
                            onClick: () => {
                              if (!selectedKeys.includes(technology.key)) {
                                toggleKey(technology.key);
                              }
                              modalHandlers.openRemoveTagModal();
                            },
                          },
                          {
                            label: '' as const,
                            isDivider: true,
                          },
                        ]
                      : []),
                    {
                      label: 'Export',
                      onClick: () => {
                        if (!selectedKeys.includes(technology.key)) {
                          toggleKey(technology.key);
                        }
                        // Export will happen with next render when selection is updated
                        setTimeout(() => modalHandlers.openExportModal(), 100);
                      },
                    },
                  ],
                },
              };
            }}
            sortableColumns={{
              activeColumns,
              setActiveColumns,
              defaultConfig: sortableColumnsConfig,
              placeholder,
            }}
            sort={
              filter?.sort && {
                ...filter.sort,
                onSort: sort => {
                  setFilter(prev => ({ ...prev, sort }));
                },
              }
            }
            selection={{
              value: selectedKeys,
              onChange: setSelectedKeys,
            }}
            rowHeight={64}
            name="technologies"
            columns={columns}
            data={technologies}
            error={technologiesError}
            status={technologiesStatus}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            noData={getTechnologyNoDataConfig({
              isAllEmpty,
              isFilteredEmpty,
              hasActiveFilters,
              onClearFilters: () =>
                setFilter(prev => ({
                  ...prev,
                  search: undefined,
                  properties: undefined,
                })),
              onGetStarted: canWriteAssets
                ? () => setIsGetStartedModalOpen(true)
                : undefined,
            })}
          />

          {/* Modals - Migrated to shared NoteModal (2025-10-23) */}
          <NoteModal
            open={modalState.noteModal}
            onClose={modalHandlers.closeNoteModal}
            onSubmit={handlers.handleNote}
            resourceType="Technology"
            mode="markdown"
            presentation="modal"
            selectedCount={selectedRowsCount}
          />

          <TechnologyTag
            open={modalState.tagModal}
            onClose={modalHandlers.closeTagModal}
            technologyKeys={selectedKeys}
            currentTags={[]}
            onSuccess={clearSelection}
          />

          <TechnologyRemoveTag
            open={modalState.removeTagModal}
            onClose={modalHandlers.closeRemoveTagModal}
            technologyKeys={selectedKeys}
            onSuccess={clearSelection}
          />

          <UniversalExportModal
            config={technologyExportConfig}
            open={modalState.exportModal}
            onClose={modalHandlers.closeExportModal}
            selectedRows={
              (isSelectAll
                ? []
                : selectedKeys) as unknown as readonly import('@/components/export/universal/types').TechnologyKey[]
            }
            onClearSelection={clearSelection}
            activeFilters={exportActiveFilters}
            activeQuery={activeQuery as GraphQuery | undefined}
          />

          {/* GetStartedModal workflow - onboarding modals for first-time users */}
          <GetStartedModal
            open={isGetStartedModalOpen}
            onClose={() => setIsGetStartedModalOpen(false)}
            onSelectMethod={handleGetStartedMethodSelect}
            title="Get Started with Technologies"
            subtitle="Technologies are discovered when you populate your attack surface:"
            methods={ASSET_GET_STARTED_METHODS}
          />

          <AddSeedByTypeModal
            open={isAddSeedModalOpen}
            onClose={() => setIsAddSeedModalOpen(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({
                predicate: query => {
                  const queryKey = query.queryKey as string[];
                  return queryKey[0] === 'MY' && queryKey[1] === 'technology';
                },
                refetchType: 'active',
              });
              setIsAddSeedModalOpen(false);
            }}
          />

          <ImportAssetsModal
            open={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            onImport={() => {
              queryClient.invalidateQueries({
                predicate: query => {
                  const queryKey = query.queryKey as string[];
                  return queryKey[0] === 'MY' && queryKey[1] === 'technology';
                },
                refetchType: 'active',
              });
              setIsImportModalOpen(false);
            }}
          />
        </>
      )}
    </AppRouteBody>
  );
}
